import { isIosNativeAppRuntime } from './runtimeCapabilities';
import { markIosAccessSessionGranted } from './iosAccessSession';

const ENABLED_VALUES = new Set(['1', 'true', 'yes', 'on', 'enabled']);
/** Session-only so each cold start shows the access-code / paywall gate again. */
const IOS_REVIEW_ACCESS_STORAGE_KEY = 'ziawellnav_ios_review_access';
/** Legacy key (localStorage, 30-day). Migrated away so testing is not stuck unlocked. */
const IOS_REVIEW_ACCESS_LEGACY_KEY = 'ziawellnav_ios_review_access';

type IosReviewAccessRecord = {
  mode: 'app_review_demo';
  unlockedAt: string;
  /**
   * The code the user typed, kept so it can be sent as
   * X-ZiaWellNav-Review-Access on protected requests.
   *
   * Session storage only, and it is a code the user already knows — this is
   * carrying their own input, not distributing a secret. The build no longer
   * embeds any code list, so the bundle reveals nothing.
   */
  code: string;
};

/**
 * Static reads only. A dynamic lookup silently returns nothing in production.
 *
 * This was `import.meta.env?.[name]`, and Vite only substitutes DIRECT member
 * access — a computed lookup is left alone, and the runtime object Vite emits
 * holds just { BASE_URL, DEV, MODE, PROD, SSR }. Verified in the shipped
 * bundle: the minified function reads that five-key literal, and no VITE_*
 * key appears anywhere in it.
 *
 * So every value below came back '' in every production build, which made
 * isIosReviewAccessConfigured() permanently false: no unlock field, no
 * successful unlock, and getIosReviewAccessHeaderValue() always null so the
 * X-ZiaWellNav-Review-Access header was never sent. The whole App Review path
 * was inert no matter what was set at build time.
 */
const BUILD_ENV: Record<string, string> = {
  VITE_IOS_REVIEW_ACCESS: import.meta.env.VITE_IOS_REVIEW_ACCESS ?? '',
};

function getEnvValue(name: string): string {
  return String(BUILD_ENV[name] ?? '').trim();
}

function isReviewAccessFlagEnabled(): boolean {
  return ENABLED_VALUES.has(getEnvValue('VITE_IOS_REVIEW_ACCESS').toLowerCase());
}

function normalizeReviewCode(value: string): string {
  return value.trim().replace(/\s+/g, '').toUpperCase();
}

// The build no longer reads any code list — VITE_IOS_REVIEW_ACCESS is a plain
// on/off flag for whether to offer the unlock field at all. The codes
// themselves live only in the server's IOS_REVIEW_ACCESS_CODES.
//
// These are ACCESS codes, not discount codes — they let someone in without
// paying, they do not reduce a price. This comment used to say "promo", which
// is where the confusion with the (now deleted) /api/subscription/promo
// discount system came from. There is no discount-code system in the app;
// Stripe Checkout's own allow_promotion_codes handles coupons.
/**
 * The code list is NOT shipped to the client. The server owns it.
 *
 * Building the list here would mean baking VITE_IOS_REVIEW_ACCESS_CODE into
 * the bundle, where anyone who downloads the app can read it — and if it
 * matched the server's IOS_REVIEW_ACCESS_CODES, reading it would buy real API
 * access, not just a local paywall skip.
 *
 * It is also unnecessary. _middleware.js already validates the
 * X-ZiaWellNav-Review-Access header against the server list on every request,
 * so the client's only job is to carry what the user typed. A local comparison
 * was a second gate that added no security (any client check is bypassable)
 * and forced the secret into the bundle to exist at all.
 */
function isReviewCodeShaped(value: string): boolean {
  return normalizeReviewCode(value).length >= 4;
}

export function isIosReviewAccessConfigured(): boolean {
  // The flag alone decides whether to offer the field. Whether a given code is
  // right is the server's answer, not ours.
  return isIosNativeAppRuntime() && isReviewAccessFlagEnabled();
}

/**
 * Drop legacy localStorage unlocks so a prior "30-day" unlock cannot permanently
 * bypass the gate while testing App Review / link-purchase flows.
 */
export function purgeLegacyIosReviewAccess(): void {
  try {
    window.localStorage.removeItem(IOS_REVIEW_ACCESS_LEGACY_KEY);
  } catch {
    // Best effort.
  }
}

export function unlockIosReviewAccess(code: string): { success: boolean; reason?: 'not_configured' | 'invalid_code' | 'storage_unavailable' } {
  if (!isIosReviewAccessConfigured()) return { success: false, reason: 'not_configured' };
  /*
   * Shape only. A wrong code is rejected by the server on the first protected
   * request, which is the check that was always doing the real work — the old
   * local comparison could only ever have been a convenience, and it cost the
   * secret's confidentiality to provide.
   */
  if (!isReviewCodeShaped(code)) {
    return { success: false, reason: 'invalid_code' };
  }

  try {
    const record: IosReviewAccessRecord = {
      mode: 'app_review_demo',
      unlockedAt: new Date().toISOString(),
      code: normalizeReviewCode(code),
    };
    // Session only — force-quit / cold start requires the code again (good for TF testing + ASC notes).
    window.sessionStorage.setItem(IOS_REVIEW_ACCESS_STORAGE_KEY, JSON.stringify(record));
    // Clear any old durable unlock so it cannot fight session lock.
    purgeLegacyIosReviewAccess();
    markIosAccessSessionGranted();
    return { success: true };
  } catch {
    return { success: false, reason: 'storage_unavailable' };
  }
}

export function isIosReviewAccessUnlocked(_refreshToken?: number): boolean {
  if (!isIosReviewAccessConfigured()) return false;
  // Never honor durable localStorage unlocks.
  purgeLegacyIosReviewAccess();
  try {
    const raw = window.sessionStorage.getItem(IOS_REVIEW_ACCESS_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<IosReviewAccessRecord>;
    if (parsed.mode !== 'app_review_demo' || typeof parsed.unlockedAt !== 'string') {
      clearIosReviewAccess();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

// The code to send to the backend so protected/company endpoints unlock for the
// App Review demo. Returns null unless review access is configured AND unlocked.
export function getIosReviewAccessHeaderValue(): string | null {
  if (!isIosReviewAccessUnlocked()) return null;
  // Whatever the user typed, verbatim — the server compares it to its own list.
  try {
    const raw = window.sessionStorage.getItem(IOS_REVIEW_ACCESS_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as IosReviewAccessRecord) : null;
    return parsed?.code || null;
  } catch {
    return null;
  }
}

export function clearIosReviewAccess(): void {
  try {
    window.sessionStorage.removeItem(IOS_REVIEW_ACCESS_STORAGE_KEY);
  } catch {
    // Local review access is best effort only.
  }
  purgeLegacyIosReviewAccess();
}
